/* eslint-disable array-callback-return */
/* eslint-disable react-hooks/exhaustive-deps */
import {
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { withStyles } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import Spinner from 'modules/common/spinner.component';
import AddressAutocompleteSection from './addressManagementCrudTable.component';

const styles = theme => ({
  editModalContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100% !important',
    marginLeft: '0px !important'
  },
  editModalColumn: {
    display: 'flex',
    flexDirection: 'column',
    width: '100% !important'
  },
  editModalInput: {
    minWidth: '332px'
  },
  dialogContentContainer: {
    margin: '15px'
  }
});

const chunkColumns = (columns, chunkSize) => {
  const rows = [];
  for (let i = 0; i < columns.length; i += chunkSize) {
    rows.push(columns.slice(i, i + chunkSize));
  }
  return rows;
};

// Edit Modal for Address Management with Google Places Integration
const AddressEditRecordModal = ({ open, classes, columns, onClose, row, onSubmit, hideEdit, isLoading, countriesEnum, provincesEnum }) => {
  // Seed from row.original, not row.getValue: an enumColumn's value is its title, while the form edits the id.
  const [values, setValues] = useState(() =>
    columns.reduce((acc, column) => {
      acc[column.accessorKey ?? ''] = row.original[column.accessorKey];
      return acc;
    }, {})
  );

  const renderInput = column => {
    if (column?.hideInModel === true || (column.shouldHideField != null && column.shouldHideField(values))) {
      return null;
    }
    if (column.type === 'text') {
      return (
        <div>
          <TextField
            key={column.accessorKey}
            label={column.header}
            name={column.accessorKey}
            required={column.required}
            value={values[column.accessorKey] || ''}
            disabled={column.accessorKey === 'fieldId' ? values[column.accessorKey]?.length > 0 : column.disabled}
            margin="dense"
            variant="standard"
            sx={{ width: '360px' }}
            onChange={e => setValues({ ...values, [e.target.name]: e.target.value })}
          />
          {column.required && (
            <Typography variant="caption" display="block" color="primary">
              Required
            </Typography>
          )}
        </div>
      );
    }
    if (column.type === 'dropdown-v2') {
      const isProvinceField = column.accessorKey === 'provinceId';
      const isCountryField = column.accessorKey === 'countryId';
      const isDisabled = column.disabled || (isProvinceField && !values.countryId);

      // Filter provinces by selected country and sort options alphabetically
      let options = column.options || [];
      if (isProvinceField && values.countryId) {
        options = provincesEnum.filter(pe => pe.countryId === values.countryId);
      }
      // Sort with United States first, then alphabetically
      options = [...options].sort((a, b) => {
        if (a.title === 'United States') return -1;
        if (b.title === 'United States') return 1;
        return a.title.localeCompare(b.title);
      });
      return (
        <div>
          <FormControl sx={{ marginTop: '6px' }}>
            <InputLabel sx={{ marginLeft: '-15px', marginTop: '7px' }} id={column.header}>
              {column.header}
            </InputLabel>
            <Select
              labelId={column.header}
              key={column.accessorKey}
              label={column.header}
              name={column.accessorKey}
              required={column.required}
              disabled={isDisabled}
              value={values[column.accessorKey] || ''}
              margin="dense"
              variant="standard"
              sx={{ marginTop: '6px', marginBottom: '6px', width: '360px' }}
              onChange={e => {
                const newValues = { ...values, [e.target.name]: e.target.value };
                // Clear province when country changes
                if (isCountryField) {
                  newValues.provinceId = '';
                }
                setValues(newValues);
              }}
            >
              {options.map(opt => (
                <MenuItem key={opt.id} value={opt.id} sx={{ margin: 'dense' }} disabled={opt.disabled}>
                  {opt.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {column.required && (
            <Typography variant="caption" display="block" color="primary">
              Required
            </Typography>
          )}
        </div>
      );
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    await onSubmit(values, row);
    onClose();
  };

  const filterVisibleColumns = columns => {
    return columns.filter(column => !column.hideInModel);
  };

  let visibleColumns = filterVisibleColumns(columns);
  let notesGroup = visibleColumns.filter(column => column.type === 'notes');
  let addedModifiedGroup = visibleColumns.filter(
    column => column.type !== 'notes' && (column.accessorKey.includes('added') || column.accessorKey.includes('modified'))
  );
  let remainingColumns = visibleColumns.filter(
    column => column.type !== 'notes' && !(column.accessorKey.includes('added') || column.accessorKey.includes('modified'))
  );

  const rows = chunkColumns(remainingColumns, 3);

  return (
    <Dialog open={open} fullWidth={true} maxWidth={'lg'}>
      <form onSubmit={handleSubmit}>
        <DialogTitle textAlign="center">Edit Address</DialogTitle>
        <div className={classes.dialogContentContainer}>
          <Stack
            sx={{
              width: '100%',
              minWidth: { xs: '300px', sm: '360px', md: '400px', lg: '400px' },
              gap: '1.5rem',
              padding: '20px'
            }}
          >
            {/* Address Autocomplete Section */}
            <AddressAutocompleteSection values={values} setValues={setValues} countriesEnum={countriesEnum} provincesEnum={provincesEnum} />

            {/* Regular Form Fields */}
            <Grid container spacing={2} direction="column">
              {rows.map((row, rowIndex) => (
                <Grid container item spacing={2} key={rowIndex}>
                  {row.map((column, colIndex) => (
                    <Grid item xs={4} key={column.accessorKey}>
                      {renderInput(column)}
                    </Grid>
                  ))}
                </Grid>
              ))}
            </Grid>

            <div className={classes.editModalContainer}>
              <div className={classes.editModalColumn}>
                {notesGroup.map(column => {
                  return renderInput(column);
                })}
              </div>
            </div>
            <div className={classes.editModalContainer}>
              <div className={classes.editModalColumn}>
                {addedModifiedGroup.map(column => {
                  return renderInput(column);
                })}
              </div>
            </div>
          </Stack>
        </div>
        {isLoading && (
          <DialogActions sx={{ p: '1.25rem' }}>
            <Spinner />
          </DialogActions>
        )}
        {!isLoading && !hideEdit && (
          <DialogActions sx={{ p: '1.25rem' }}>
            <Button onClick={onClose}>Cancel</Button>
            <Button color="secondary" type="submit" variant="contained">
              Save
            </Button>
          </DialogActions>
        )}
        {!isLoading && hideEdit && (
          <DialogActions sx={{ p: '1.25rem' }}>
            <Button onClick={onClose}>Close</Button>
          </DialogActions>
        )}
      </form>
    </Dialog>
  );
};

// Create Modal for Address Management with Google Places Integration
const AddressCreateRecordModal = ({ open, classes, columns, onClose, onSubmit, isLoading, countriesEnum, provincesEnum }) => {
  const [values, setValues] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setValues(
        columns.reduce((acc, column) => {
          acc[column.accessorKey ?? ''] = column.defaultVal != null ? column.defaultVal : '';
          if (column.nameKey) acc[column.nameKey] = column.defaultName != null ? column.defaultName : '';
          return acc;
        }, {})
      );
    }
  }, [open, columns]);

  const renderInput = (column, index) => {
    if (column?.hideInModel === true || (column.shouldHideField != null && column.shouldHideField(values))) {
      return null;
    }
    if (column.type === 'text') {
      return (
        <div>
          <TextField
            key={column.accessorKey}
            label={column.header}
            name={column.accessorKey}
            required={column.required}
            disabled={column.disabled}
            sx={{ width: '360px' }}
            margin="dense"
            value={values[column.accessorKey] || ''}
            onChange={e => setValues({ ...values, [e.target.name]: e.target.value })}
            variant="standard"
          />
          {column.required && (
            <Typography variant="caption" display="block" color="primary">
              Required
            </Typography>
          )}
        </div>
      );
    }
    if (column.type === 'dropdown-v2') {
      const isProvinceField = column.accessorKey === 'provinceId';
      const isCountryField = column.accessorKey === 'countryId';
      const isDisabled = column.disabled || (isProvinceField && !values.countryId);

      // Filter provinces by selected country and sort options alphabetically
      let options = column.options || [];
      if (isProvinceField && values.countryId) {
        options = provincesEnum.filter(pe => pe.countryId === values.countryId);
      }
      // Sort with United States first, then alphabetically
      options = [...options].sort((a, b) => {
        if (a.title === 'United States') return -1;
        if (b.title === 'United States') return 1;
        return a.title.localeCompare(b.title);
      });
      return (
        <div>
          <FormControl sx={{ marginTop: '6px' }}>
            <InputLabel sx={{ marginLeft: '-15px', marginTop: '7px' }} id={column.header}>
              {column.header}
            </InputLabel>
            <Select
              labelId={column.header}
              key={column.accessorKey}
              label={column.header}
              name={column.accessorKey}
              required={column.required}
              disabled={isDisabled}
              value={values[column.accessorKey] || ''}
              margin="dense"
              variant="standard"
              sx={{ marginTop: '6px', marginBottom: '6px', width: '360px' }}
              onChange={e => {
                const newValues = { ...values, [e.target.name]: e.target.value };
                // Clear province when country changes
                if (isCountryField) {
                  newValues.provinceId = '';
                }
                setValues(newValues);
              }}
            >
              {options.map(opt => (
                <MenuItem key={opt.id} value={opt.id} sx={{ margin: 'dense' }} disabled={opt.disabled}>
                  {opt.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {column.required && (
            <Typography variant="caption" display="block" color="primary">
              Required
            </Typography>
          )}
        </div>
      );
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setIsSubmitting(true);
    await onSubmit(values);
    setIsSubmitting(false);
    onClose();
  };

  const filterVisibleColumns = columns => {
    return columns.filter(column => !column.hideInModel);
  };

  let visibleColumns = filterVisibleColumns(columns);
  let notesGroup = visibleColumns.filter(column => column.type === 'notes');
  let addedModifiedGroup = visibleColumns.filter(
    column => column.type !== 'notes' && (column.accessorKey.includes('added') || column.accessorKey.includes('modified'))
  );
  let remainingColumns = visibleColumns.filter(
    column => column.type !== 'notes' && !(column.accessorKey.includes('added') || column.accessorKey.includes('modified'))
  );

  const rows = chunkColumns(remainingColumns, 3);

  return (
    <Dialog open={open} fullWidth={true} maxWidth={'lg'}>
      <form onSubmit={handleSubmit}>
        <DialogTitle textAlign="center">Add Address</DialogTitle>
        <div className={classes.dialogContentContainer}>
          <Stack
            sx={{
              width: '100%',
              minWidth: { xs: '300px', sm: '360px', md: '400px', lg: '400px' },
              gap: '1.5rem',
              padding: '20px'
            }}
          >
            {/* Address Autocomplete Section */}
            <AddressAutocompleteSection values={values} setValues={setValues} countriesEnum={countriesEnum} provincesEnum={provincesEnum} />

            {/* Regular Form Fields */}
            <Grid container spacing={2} direction="column">
              {rows.map((row, rowIndex) => (
                <Grid container item spacing={2} key={rowIndex}>
                  {row.map((column, colIndex) => (
                    <Grid item xs={4} key={column.accessorKey}>
                      {renderInput(column, rowIndex * colIndex)}
                    </Grid>
                  ))}
                </Grid>
              ))}
            </Grid>

            <div className={classes.editModalContainer}>
              <div className={classes.editModalColumn}>
                {notesGroup.map((column, index) => {
                  return renderInput(column, index);
                })}
              </div>
            </div>
            <div className={classes.editModalContainer}>
              <div className={classes.editModalColumn}>
                {addedModifiedGroup.map((column, index) => {
                  return renderInput(column, index);
                })}
              </div>
            </div>
          </Stack>
        </div>
        {isLoading && (
          <DialogActions sx={{ p: '1.25rem' }}>
            <Spinner />
          </DialogActions>
        )}
        {!isLoading && (
          <DialogActions sx={{ p: '1.25rem' }}>
            <Button onClick={onClose}>Cancel</Button>
            <Button color="secondary" type="submit" variant="contained" sx={{ color: 'white' }} disabled={isSubmitting}>
              Add Address
            </Button>
          </DialogActions>
        )}
      </form>
    </Dialog>
  );
};

const AddressEditRecordModalStyled = withStyles(styles)(AddressEditRecordModal);
const AddressCreateRecordModalStyled = withStyles(styles)(AddressCreateRecordModal);

export { AddressEditRecordModalStyled as AddressEditRecordModal, AddressCreateRecordModalStyled as AddressCreateRecordModal };
export default AddressCreateRecordModalStyled;
